export enum EUserPermission {
  /** 시스템 관리자 */
  SYSADMIN = 'SYSADMIN',
  /** 일반 관리자 */
  ADMIN = 'ADMIN',

  /** 자문위원 (교수) */
  ADVISOR = 'ADVISOR',
  /** 자문위원 (연구원) */
  CONSULTANT = 'CONSULTANT',

  /** 일반 창작자(강사) */
  CREATOR = 'CREATOR',
  /** 일반 사용자 */
  MEMBER = 'MEMBER',

  /** 비회원 */
  VISITOR = 'VISITOR',
}

export enum ESocialProvider {
  GOOGLE = 'google',
  FACEBOOK = 'facebook',
  KAKAO = 'kakao',
}

export interface IUserProfile {
  uuid: string;
  name: string;
  email: string;
  avatarUrl: string;
  permission: EUserPermission,
  profileBackgroundUrl: string;
  socialProvider: ESocialProvider[];
  createdAt: Date;
  agreeAt: Date;
  lastActionAt?: Date;
  money: {
    point: number;
    mileage: number;
  }
  contentsCnt: number;
  classRoomsCnt: number;
  followerCnt: number;
  shortIntroduction: string;
  longIntroduction: string;
  phone: string;
  actualName: string;
  belong: string;
}

export interface IUserSpecProfile extends IUserProfile {
  emailVerifiedAt: string;
  isDeleted: boolean;
  isVerified: boolean;
  isEnabled: boolean;
  isSvcAccExist: boolean;
  belong: string;
  actualName: string;
  phone: string;
  socialEmail: string;
  willBeDeletedAt: Date;
  updatedAt: Date;
}

export enum EContentType {
  MATH_2D = 'application/math2d',
  ALGEBRA_3D = 'application/algebra3d',
  SCIENCE_2D = 'application/science2d',
  MATH_CUBE = 'application/genicube',
  FIGURE_3D = 'application/figure3d',
  WORK_BOOK = 'application/workbook',
  STEAM_AI = 'application/steamai',
  STEAM_XR = 'application/steamxr',
  STEAM_MOLE = 'application/steammole',
  OBSERVATORY = 'application/observatory',
  // TODO: check - 통계 도구 추가 -> 추후 수정 가능함
  STATISTIC_TOOLS = 'application/statistic',
  FRACTOPIA = 'application/fractopia',
  MATH_3D = 'application/math3d',
}

export type IContentsMetaData = {
  version: string;
  locale: string;
  description: string;
  curriculum: number;
  keywords: number[];
  userAgent: string;
  errorLog: string;
  grade: number;
  subject: number;
}

interface IFiles {
  uuid: string;
  name: string;
  createdAt: Date;
  updatedAt?: Date;
}

export interface IDocumentFile extends IFiles {
  type: 'file';
  contentType: EContentType;
  thumbnail: string;
  exportable: boolean;
  readable: boolean;
  viewCnt: number;
  downloadCnt: number;
  shareCnt: number;
  likeCnt: number;
  isEditable: boolean;
  isPurchased: boolean;
  purchaseConfirm: boolean;
  storePurchaseHistoryUUID: string;
}

export interface IDocumentFolder extends IFiles {
  type: 'folder';
  children: IDocument[];
}

export type IDocument = IDocumentFile | IDocumentFolder;

export type DocumentSortType = 'name' | 'created' | 'updated';
export type DocumentSortDirection = 'DESC' | 'ASC';

export type DocumentSort = {
  type: DocumentSortType;
  direction: DocumentSortDirection;
}

export interface NaviData {
  uuid: string;
  name: string;
}

export type TranslationContent = Record<string, string>;

export interface AttachedFile {
  src: string;
  fileName: string;
}
export interface IPublicBoard {
  uuid: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date;
  boardFaqTypeID: number;
  creator?: string;
  title: TranslationContent;
  content: TranslationContent;
  isEnabled?: boolean;
  isStrong: boolean;
  viewCnt?: number;
  attachedFile: AttachedFile[];
}

export type PublicBoardName = 'notice' | 'library' | 'qna' | 'faq';

export interface IPublicBoardOne {
  loading: boolean;
  total: number;
  data: IPublicBoard[];
  strongData?: IPublicBoard[];
}

export interface IKeyword {
  id: number;
  name: string;
  key: string;
}

export interface ICommonKeywords {
  [groupName: string]: IKeyword[]
}

export interface ICommonGrades {
  id: number;
  name: string;
  key: string;
}

export interface ICommonSubjects {
  id: number;
  name: string;
  key: string;
}

export interface ICommonLevels {
  id: number;
  name: string;
  key: string;
}

export interface ICommonTutorialTypes {
  id: number;
  title: string;
  key: string;
  content_order: number;
}

export interface ICommonTutorialTypeList {
  data: ICommonTutorialTypes[];
}

export interface ICommonStoreCategory {
  id: number;
  title: string;
  key: string;
  storeCategoryOrder: number;
}

export interface ICommonStoreCategoryList {
  data: ICommonStoreCategory[];
}

export interface ICommonBoardFaqTypes {
  id: number;
  title: string;
  key: string;
}

export interface ICommonBoardFaqTypeList {
  data: ICommonBoardFaqTypes[];
}

export interface ICommonCurriculums {
  id: number;
  grade: number;
  subject: number;
  name: string;
  key: string;
  children: ICommonCurriculums[];
}

export enum ETokenIcoCurrentStatus {
  FEASIBLE = 'FEASIBLE',
  INFEASIBLE = 'INFEASIBLE',
  STARTED_NO_CONTRACT_ADDRESS = 'STARTED_NO_CONTRACT_ADDRESS',
  STARTED_NOT_APPROVED = 'STARTED_NOT_APPROVED',
  STARTED_NOT_SWAPPED = 'STARTED_NOT_SWAPPED',
  STARTED_APPROVED_DEFICIENCY = 'STARTED_APPROVED_DEFICIENCY',
}

export interface IProfile {
  uuid: string;
  name: string;
  avatarUrl: string;
  hasFollow: boolean;
  createdAt: Date;
  shortIntroduction: string;
  longIntroduction: string;
  profileBackgroundUrl: string;
}

export interface IMajor {
  majorClass: {
    majorClassThumbnail: {
      images: string[];
      main: string;
    }
    majorClassUUID: string;
    majorClassTitle: string;
  }[];
  majorContent: {
    majorContentThumbnail: string;
    majorContentUUID: string;
    majorContentTitle: string;
  }[];
}

export interface IContentsData {
  uuid: string;
  title: string;
  createdAt: Date;
  thumbnail: string;
  meta: IContentsMetaData;
  contentType: EContentType;
  likeCnt: number;
  downloadCnt: number;
  shareCnt: number;
  viewCnt: number;
  commentCnt: number;
  hasCommented: boolean;
  hasLiked: boolean;
  hasFollow: boolean;
  saveDataURL: string;
  exportable: boolean;
  creator: IProfile;
  major: IMajor;
  price: number;
  isPurchased: boolean;
  isMajor: boolean;
  // 새로 추가한 부분
  readable: boolean;
  updatedAt?: Date;
  order?: number;

}

export interface ILikedMemberData {
  uuid: string;
  followee_info: IFolloweeInfo[];
}

export interface IFolloweeInfo {
  avatarUrl: string;
  name: string;
  followerCnt: number;
}
export interface IContentsComment {
  uuid: string;
  createdAt: Date;
  isUpdated: boolean;
  content: string;
  creator: IProfile;
  major: IMajor;
}

export enum EAuthModalPage {
  /** 이메일 입력 */
  INPUT_EMAIL,
  /** 비밀번호 입력 */
  INPUT_PASSWORD,

  /** 이메일 인증 전송 & 인증코드 입력 */
  SEND_EMAIL_AND_INPUT_CONFIRM_CODE,
  /** 새 비밀번호 입력 */
  INPUT_NEW_PASSWORD,

  /** 비밀번호 입력 5회 이상 실패시 안내 */
  WRONG_PASSWORD,
  /** 비밀번호찾기 - 새 비밀번호 입력 */
  FIND_PASSWORD_INPUT_NEW_PASSWORD,

  /** 소셜 - 이메일 인증 안되어 있을때 이메일 입력 */
  INPUT_EMAIL_SOCIAL,

  /** 이용약관, 개인정보처리방침 동의 */
  ARGREE_POLICY,

  /** 예외 - 삭제예정 */
  WILL_BE_DELETED_ACCOUNT,

  /** 로그인 완료 */
  SUCCESS,

  /** 추가한 페이지 */
  /** 회원 선택 - 일반/강사 */
  SELECT_USER,
  /** 일반 회원가입 (일반, 강사 모두) */
  SIGN_UP,
  /** 강사 회원가입 */
  CREATOR_SIGN_UP,
  /** 이메일 인증 */
  AUTH_EMAIL,
  /** 회원가입 완료 */
  COMPLETE,
  /**  비밀번호 재설정 */
  RESET_PASSWORD,
  /** 비밀번호 재설정 후 모바일 화면 */
  COMPLETE_PASSWORD_MOBILE
}

export interface ICreators {
  creator: IProfile;
  major: IMajor;
  downloadCnt: number;
  likeCnt: number;
  viewCnt: number;
  contentsCnt: number;
  commentCnt: number;
  followerCnt: number;
  majorContentThumbnail: string;
}

export interface IFollowList extends IProfile {
  hasFollow: boolean;
  followerCnt: number;
  contentsCnt: number;
}

export enum EPointHistoryReason {
  INISIS_1000 = 'INISIS-1000',
  INISIS_2000 = 'INISIS-2000',
  INISIS_5000 = 'INISIS-5000',
  INISIS_10000 = 'INISIS-10000',

  FIRST_JOIN = 'FIRST-JOIN',
  ATTENDANCE = 'ATTENDANCE',
  DOWNLOAD_AWARDS = 'DOWNLOAD-AWARDS',

  PURCHASE_CONTENTS = 'PURCHASE_CONTENTS',
  PURCHASE_STORE = 'PURCHASE_STORE',
  SALES_PROFIT_STORE_PURCHASE_HISTORY_ID = 'SALES-PROFIT_STORE_PURCHASE_HISTORY_ID',
  REFUND_STORE_PURCHASE_HISTORY_ID = 'REFUND_STORE_PURCHASE_HISTORY_ID',
  PURCHASE_STORE_SALES_TYPE = 'PURCHASE_STORE_SALES_TYPE',
  EXPIRED = 'EXPIRED',
}
export interface IPointHistory {
  createdAt: Date;
  category: 'mileage' | 'point';
  diffPoint: number;
  resultPoint: number;
  reason: EPointHistoryReason;
}

export interface IPointProduct {
  uuid: string;
  title: EPointHistoryReason;
  point: number;
}

export interface IParentProcess {
  uuid: string;
  title: string;
  processOrder: number;
  subProcessData: ISubProcess[];
}

export interface ISubProcess {
  uuid: string;
  title: string;
  processOrder: number;
  data: {
    data1: string;
    data2?: string;
  }
  isComplete: boolean;
  isTempComplete: boolean;
  contentSourceID: number;
}

export interface IClassRoomData extends IClassRoomContent {
  uuid: string;
  createdAt: Date;
  updatedAt: Date;
  postedAt: Date;
  subject: number[];
  grade: number[];
  level: number;
  price: number;
  expiredAt: number;
  viewCnt: number;
  progress: number;
  rating: number;
  keywords: number[];
  isPost: boolean;
  inviteCode: string;
  classRoomProcess?: IParentProcess[];
  prefixAlias?: string;
}

export interface IClassRoomDataDetail extends IClassRoomData {
  creatorUUID: string;
  creatorAvatarUrl: string;
  creatorName: string;
  isPurchased: boolean;
  isBookmark: boolean;
  purchasedCreatedAt: Date | number;
  purchasedExpiredAt: number;
  userCnt: number;
}

export interface IClassRoomContent {
  title: string;
  subTitle: string;
  content: string;
  thumbnail: {
    main: string;
    images: string[];
  }
}

export interface IClassRoomReview {
  uuid: string;
  createdAt: Date;
  isUpdated: boolean;
  review: string;
  level: number;
  utilization: number;
  rating: number;
  creator: IProfile;
  major?: IMajor;
}

export interface IClassRoomRate {
  id: number;
  cnt: number;
  name: string;
  rate: number;
  translation: { en: string };
}

export interface IClassRoomInstructor {
  uuid: string;
  avatarURL: string;
  name: string;
  shortIntroduction: string;
  belong: string;
  postsCnt: number;
  followerCnt: number;
  reviewCnt: number;
  hasFollow: boolean;
}

export interface IClassRoomProcess {
  title: string;
  parentOrder: number;
  subOrder: number;
  saveDataURL: string;
  contentType: string;
  lastData: {};
}

export interface IClassRoomQnA {
  uuid: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date;
  answeredAt: Date;
  name: string;
  memberUUID: string;
  location: string;
  questionContent: {
    content: string;
    files: {fileName: string; src: string}[]
    // files: (string|File)[];
  };
  answerContent: {
    content: string;
    files: {fileName: string; src: string}[]
    // files: (string|File)[];
  };
  isPrivate: boolean;
  isEmail: boolean;
  isClassOrQnaCreator: boolean;
}

export interface IClassRoomShortcutsQnA extends IClassRoomQnA {
  title: string;
  parentOrder: number;
  subOrder: number;
  saveDataURL: string;
  lastData: {};
}

export interface ICreatedClass {
  createdClassRoomDataList: IClassRoomData[];
  createdClassRoomTotalNo: number;
  publicClassCnt: number;
  privateClassCnt: number;
  createdCnt: number;
}

export interface IAdminTutorialContent {
  id: number;
  uuid: string;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date;
  title: string;
  description: string;
  tutorialContentImagePath: string;
  tutorialContentURI: string;
  contentType: string;
  tutorialTypeID: number;
  level: number;
  translation: {
    title: string;
    description: string;
    tutorialContentURI: string;
  };
  isReadable: boolean;
  memberUUID: string;
  memberName: string;
  memberEmail: string;
}

export interface ITutorialContent {
  id: number;
  uuid: string;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date;
  title: string;
  description: string;
  tutorialContentImagePath: string;
  level: number;
  translation: {
    title: string;
    description: string;
    tutorialContentURI: string;
  };
  isReadable: boolean;
  typeTitle: string;
}

export interface ITutorialContentDetail extends ITutorialContent {
  tutorialContentURI: string;
  translation: {
    title: string;
    description: string;
    tutorialContentURI: string;
  };
}

export interface IEmailQuestion {
  id: number;
  createdAt: Date;
  answeredAt?: Date;
  email: string;
  questionInfo: {
    ip: string;
    title: string;
    content: string;
    userAgent: string;
    questionType: string;
  };
  answerInfo: {
    content: string;
    answerer: string;
  };
  answererName: string;
  questionerName: string;
}

export interface IStore {
  uuid: string; // storeUUID
  creatorName: string; // 판매자 이름
  creatorUUID: string; // 판매자 UUID
  creatorAvatarURL: string; // 판매자 프로필
  hasFollow: boolean; // 판매자 팔로우 여부 t: 팔로우, f: 언팔로우
  createdAt: Date; // 생성일
  updatedAt: Date; // 수정일
  storeContentType: EContentType; // 학습 도구
  title: string; // 상품 제목
  salesTypes: IStoreSalesTypes[]; // 상품 유형
  keywords: number[]; // 태그
}

export interface IStoreSalesFiles {
  fileName: string; // 파일명
  fileURI: string; // 파일 경로
}

export interface IStoreSalesTypes {
  uuid: string; // salesTypeUUID
  purchasedAt: Date; // 구매일
  purchaseConfirmedAt: Date; // 구매확정일
  thumbnail: string; // 썸네일
  isFree: boolean; // 유/무료 여부
  price: number; // 상품 금액
  discount: number; // 할인 금액
  myPurchasedPrice: number; // 내가 구매한 상품 금액
  myPurchasedDiscount: number; // 내가 받은 할인 금액
  discountBeginAt: Date; // 할인 시작 기간
  discountEndAt: Date; // 할인 종료 기간
  isPurchased: boolean; // 구매여부 t: 구매, f: 비구매
  isBookmark: boolean; // 찜한여부 t: 했음, f: 안했음
  bookmarkCnt: number; // 찜한 개수
  isEditable: boolean; // 편집여부 t: 가능, f: 불가능
  isMileage: boolean; // 구매시 마일리지 사용여부 t: 사용, f: 미사용
  salesCnt: number; // 판매수
  salesTypeNumber: number; // 판매 유형 번호
  approvalList: ProcessedStatusList[]; // 환불 완료 리스트
  files: IStoreSalesFiles[];
  usedPoint: number;
  usedMileage: number;
  // requestList: ProcessedStatusList[]; // 환불 요청 리스트
  // retractList: ProcessedStatusList[]; // 환불 철회 리스트
  // rejectionLis: ProcessedStatusList[]; // 환불 거절 리스트
  // allRefundList: ProcessedStatusList[]; // 모든 환불 리스트
}

export interface IStoreDetail extends IStore {
  storeCategoryID: number; // 스토어 카테고리ID
  description: string; // 상품 설명
  purchasedCnt: number; // 구매한 개수
}

export interface IStorePurchase {
  uuid: string; // purchaseUUID
  storeUUID: string; // storeUUID
  purchasedAt: Date; // 구매일
  expiredAt: Date; // 만료일
  disabledAt: Date; // 판매 종료일
  storeCategoryID: number; // 스토어 카테고리ID
  storeContentType: EContentType; // 학습 도구
  title: string; // 상품 제목
  salesTypes: IStoreSalesTypes; // 상품 유형
  // bookmarkCnt: number; // 찜한 개수
  keywords: number[]; // 태그
  isSalesEnabled: boolean; // 판매 가능 여부 t: 판매중, f: 판매 종료

  creatorUUID: string; // 판매자 UUID
  creatorName: string; // 판매자 이름
  creatorAvatarURL: string; // 판매자 프로필
}

export interface IStoreBookmark extends IStorePurchase {
  createdAt: Date; // 찜한일
}

export interface IRefund {
  uuid: string; // 환불 히스토리 UUID
  createdAt: Date; // 생성일(요청일)
  purchasedAt: Date; // 구매일
  title: string; // 상품 제목
  content: string; // 환불 사유
  usedPoint: number; // 사용한 포인트
  usedMileage: number; // 사용한 마일리지
  // refundStatusTypeID: number; // 환불 상태 종류

  creatorUUID?: string; // 판매자 UUID
  creatorAvatarURL?: string; // 판매자 프로필
  creatorName: string; // 판매자 이름
}

export interface IRefundStore extends IRefund {
  storeCategoryID: number; // 카테고리ID
  salesTypeNumber: number; // 판매 유형 번호
}

export interface ISalesStore {
  uuid: string; // storeUUID
  createdAt: Date; // 생성일
  disabledAt: Date; // 판매 종료일
  title: string; // 상품 제목
  salesTypes: IStoreSalesTypes[]; // 상품 유형
  viewCnt: number; // 조회수
  isSalesEnabled: boolean; // 판매 가능 여부 t: 판매중, f: 판매 종료
  keywords: number[];
}

export interface ProcessedStatusList {
  uuid: string; // 환불 히스토리 UUID
  purchasedAt: number; // 구매일
  // refundRequestedAt: number; // 환불요청일
  refundedAt: number; // 환불일
  // retractedAt: number; // 철회일
  // rejectionAt: number; // 거절일
  readAt: number; // 요청건 확인일
  storeCategoryID: number; // 카테고리ID
  refundContent: string; // 환불 사유
  // rejectionContent: string; // 거절 사유
  usedPoint: number; // 사용한 포인트
  usedMileage: number; // 사용한 마일리지
  salesTypeNumber: number;// 판매 유형 번호
  // refundStatusTypeID: number;// 환불 상태 종류

  purchaserUUID: string; // 구매자 UUID
  purchaserAvatarURL: string; // 구매자 프로필
  purchaserName: string; // 구매자 이름
  purchaserEmail: string; // 구매자 계정
}

export interface ISalesClassRoom {
  uuid: string; // 클래스룸 UUID
  createdAt: Date; // 생성일
  disabledAt: Date; // 판매 종료일
  price: number; // 상품 가격
  title: string; // 상품 제목
  salesCnt: number; // 판매수
  viewCnt: number; // 조회수
  approvalList: ProcessedStatusList[]; // 환불 완료 리스트
  // requestList: ProcessedStatusList[]; // 환불 요청 리스트
  // retractList: ProcessedStatusList[]; // 환불 철회 리스트
  // rejectionList: ProcessedStatusList[]; // 환불 거절 리스트
  // allRefundList: ProcessedStatusList[]; // 모든 환불 리스트
  isSalesEnabled: boolean; // 판매 가능 여부 t: 판매중, f: 판매 종료
  keywords: number[]; // 태그
}
